import nacl from 'tweetnacl';
import * as util from 'tweetnacl-util';

export interface EncryptedMediaResult {
  encryptedBlob: Blob;
  key: string;        // base64-encoded 32-byte symmetric key
  mimeType: string;   // original MIME type, needed for decryption
}

/**
 * Encrypt a media blob with a random per-file symmetric key (NaCl secretbox).
 * The returned key must be transmitted via the E2E-encrypted message channel.
 * The encryptedBlob is safe to store in GCS — it's opaque without the key.
 */
export async function encryptMedia(blob: Blob): Promise<EncryptedMediaResult> {
  const key   = nacl.randomBytes(32);
  const nonce = nacl.randomBytes(nacl.secretbox.nonceLength);
  const data  = new Uint8Array(await blob.arrayBuffer());

  const ciphertext = nacl.secretbox(data, nonce, key);

  // Layout: [nonce (24 bytes)] [ciphertext]
  const combined = new Uint8Array(nonce.length + ciphertext.length);
  combined.set(nonce);
  combined.set(ciphertext, nonce.length);

  return {
    // Declare original MIME type so multer's type filter accepts the upload.
    // The server stores bytes as-is; it never validates actual content.
    encryptedBlob: new Blob([combined], { type: blob.type }),
    key: util.encodeBase64(key),
    mimeType: blob.type
  };
}

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

/**
 * Fetch an encrypted blob via our backend proxy (avoids GCS CORS restrictions),
 * decrypt it, and return a temporary object URL.
 * Caller is responsible for calling URL.revokeObjectURL() when done.
 */
export async function decryptMediaUrl(
  encryptedUrl: string,
  keyBase64: string,
  mimeType: string
): Promise<string> {
  const proxyUrl = `${API_BASE}/api/messages/media-proxy?url=${encodeURIComponent(encryptedUrl)}`;
  const response = await fetch(proxyUrl, { credentials: 'include' });
  if (!response.ok) throw new Error(`Failed to fetch encrypted media: ${response.status}`);

  const combined = new Uint8Array(await response.arrayBuffer());
  const nonce      = combined.slice(0, nacl.secretbox.nonceLength);
  const ciphertext = combined.slice(nacl.secretbox.nonceLength);
  const key        = util.decodeBase64(keyBase64);

  const decrypted = nacl.secretbox.open(ciphertext, nonce, key);
  if (!decrypted) throw new Error('Media decryption failed — key or data may be corrupt');

  // video/quicktime (MOV from iOS) isn't playable in Chrome/Android — map to
  // video/mp4 since iPhone videos are H.264 in a QuickTime container, which
  // browsers can decode as MP4.
  const playbackMime = mimeType === 'video/quicktime' ? 'video/mp4' : mimeType;
  const blob = new Blob([decrypted], { type: playbackMime });
  return URL.createObjectURL(blob);
}
